import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DeckViewer } from './components/deck/DeckViewer';
import { ContactForm } from './components/common/ContactForm';
import { createSampleDeck } from './utils/sampleData';
import { StakeholderType, Deck } from './types';
import { STAKEHOLDER_PROFILES } from './constants';
import { exportDeck } from './utils/exportUtils';

// Vector icon components
const PatientIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
    <path d="M12 14v7"/>
    <path d="M16 18h-8"/>
  </svg>
);

const CaregiverIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    <path d="M12 14c.5 0 1 .1 1.5.3"/>
  </svg>
);

const ClinicianIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14,2 14,8 20,8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10,9 9,9 8,9"/>
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 text-muted-teal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <polyline points="20,6 9,17 4,12"/>
  </svg>
);

const WalletIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
    <path d="M18 12a2 2 0 0 0 0 4h4v-4z"/>
  </svg>
);

const ShareIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <circle cx="18" cy="5" r="3"/>
    <circle cx="6" cy="12" r="3"/>
    <circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const AccessIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12,6 12,12 16,14"/>
  </svg>
);

// Scroll-triggered reveal — animates when the section enters the viewport
// rather than on page load, so nothing is left blank while the user scrolls.
const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = '',
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.6, delay }}
  >
    {children}
  </motion.div>
);

const Section: React.FC<{ children: React.ReactNode; className?: string; id?: string }> = ({
  children,
  className = '',
  id,
}) => (
  <section id={id} className={`py-20 sm:py-24 px-6 sm:px-8 scroll-mt-20 ${className}`}>
    {children}
  </section>
);

// Compact version of the hero lockup, sized for the sticky header.
const Wordmark: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span className={`flex items-baseline gap-1.5 ${className}`}>
    <span className="text-xl leading-none font-light text-soft-black tracking-tight">
      HealthyMitra
    </span>
    <img
      src="/images/Company logo butterfly.png"
      alt=""
      aria-hidden="true"
      className="h-[1.6rem] w-auto shrink-0"
    />
  </span>
);

// One navigation for the whole site. The same links appear on every page so
// that the header never rearranges itself under the visitor; "How it works"
// points at the detail page rather than the landing section, and the
// remaining entries are landing anchors that work from any route.
const NAV_LINKS = [
  { label: 'How it works', to: '/how-it-works' },
  { label: 'Who it’s for', to: '/#who-its-for' },
  { label: 'Why trust us', to: '/#trust' },
  { label: 'Pricing', to: '/#pricing' },
  { label: 'Become a partner', to: '/partners' },
];

// Sticky site header. The pages run long, so the primary call to action
// needs to stay reachable rather than living only in the hero and pricing.
const SiteHeader: React.FC<{ onJoin: () => void }> = ({ onJoin }) => (
  <header className="sticky top-0 z-40 bg-off-white/85 backdrop-blur-md border-b border-warm-gray/15">
    <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between gap-4">
      <Link
        to="/"
        className="shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-muted-teal/40"
        aria-label="HealthyMitra — home"
      >
        <Wordmark />
      </Link>

      {/* Five entries no longer fit beside the wordmark and the button at the
          medium breakpoint, so the nav appears from large upwards. */}
      <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
        {NAV_LINKS.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="animated-text-link text-sm text-warm-gray"
          >
            {link.label}
          </Link>
        ))}
      </nav>

      <div className="shrink-0 flex items-center gap-3 sm:gap-4">
        {/* The app itself lives on its own domain; this is an external link,
            not a router route, and opens in the same tab. */}
        <a
          href="https://www.healthymitra.com"
          className="animated-text-link hidden sm:inline text-sm text-warm-gray"
        >
          Sign in
        </a>

        <button
          onClick={onJoin}
          className="px-4 sm:px-5 py-2.5 rounded-xl bg-muted-teal text-white text-sm font-medium hover:bg-muted-teal/90 transition-colors focus:outline-none focus:ring-2 focus:ring-muted-teal/40"
        >
          Join Early Access
        </button>
      </div>
    </div>
  </header>
);

const FOOTER_LINK = 'animated-text-link text-warm-gray';

const SiteFooter = () => (
  <footer className="border-t border-warm-gray/20 bg-off-white">
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-14 sm:py-16">
      {/* Two tracks from the smallest screens up, so Explore and Get in touch
          sit side by side on phones. The brand spans the full width on
          mobile and two of the five on desktop, keeping its description at a
          comfortable measure. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-10 lg:gap-8">
        <div className="col-span-2">
          <Link to="/" className="inline-block">
            <Wordmark />
          </Link>
          <p className="mt-5 text-warm-gray leading-relaxed max-w-xs">
            Structured support between consultations, for patients, caregivers
            and the clinicians who look after them.
          </p>
          <p className="mt-5 text-sm text-warm-gray/70">A product of AarogyalinQ</p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-deep-slate mb-4">Explore</h3>
          <ul className="space-y-3">
            <li><Link to="/how-it-works" className={FOOTER_LINK}>How it works</Link></li>
            <li><Link to="/#who-its-for" className={FOOTER_LINK}>Who it&rsquo;s for</Link></li>
            <li><Link to="/#trust" className={FOOTER_LINK}>Why trust us</Link></li>
            <li><Link to="/#pricing" className={FOOTER_LINK}>Pricing</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-medium text-deep-slate mb-4">Get in touch</h3>
          <ul className="space-y-3">
            <li>
              {/* Keep the address compact at the lg breakpoint, where the
                  five-column footer is narrow, and restore the base size on
                  wider desktop screens. */}
              <a
                href="mailto:contact@easemymed.com"
                className={`${FOOTER_LINK} whitespace-nowrap text-sm xl:text-base`}
              >
                contact@easemymed.com
              </a>
            </li>
            <li><a href="https://www.healthymitra.com" className={FOOTER_LINK}>Sign in</a></li>
            <li><Link to="/partners" className={FOOTER_LINK}>Become a partner</Link></li>
            <li><Link to="/how-it-works#questions" className={FOOTER_LINK}>Questions</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-medium text-deep-slate mb-4">Legal</h3>
          <ul className="space-y-3">
            <li><a href="https://www.healthymitra.com/terms" className={FOOTER_LINK}>Terms of Use</a></li>
            <li><a href="https://www.healthymitra.com/privacy" className={FOOTER_LINK}>Privacy Policy</a></li>
            <li><a href="https://www.healthymitra.com/payments-refunds" className={FOOTER_LINK}>Payments &amp; Refunds</a></li>
            <li><a href="https://www.healthymitra.com/contact-grievance" className={FOOTER_LINK}>Contact &amp; Grievance</a></li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-warm-gray/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-warm-gray/70">
        <p>&copy; {new Date().getFullYear()} AarogyalinQ. All rights reserved.</p>
        <p>Patent Pending</p>
      </div>
    </div>
  </footer>
);

const SectionHeading: React.FC<{ children: React.ReactNode; subtitle?: string }> = ({
  children,
  subtitle,
}) => (
  <Reveal className="text-center mb-14 sm:mb-16">
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-soft-black leading-tight">
      {children}
    </h2>
    {subtitle && (
      <p className="text-lg sm:text-xl text-warm-gray mt-6 max-w-2xl mx-auto leading-relaxed">
        {subtitle}
      </p>
    )}
  </Reveal>
);

type FormType = 'partnership' | 'contact' | 'pilot' | 'clinical' | 'research' | 'investment' | 'waitlist';

// Keeps the browser's scroll position honest across route changes: a new
// route opens at the top, while a link carrying a hash scrolls to that
// section. The retry covers the frame before the target has mounted.
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  // Null on the first render, so a deep link arriving with a hash counts as
  // a route change and jumps straight there rather than visibly scrolling
  // the whole page down.
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    const changedRoute = previousPath.current !== pathname;
    previousPath.current = pathname;

    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }

    let frames = 0;
    const findTarget = () => {
      const target = document.querySelector(hash);
      if (target) {
        // Arriving from another route has to jump instantly. A smooth scroll
        // drags the viewport through every section on the way, interrupting
        // each one's scroll-triggered reveal; because those reveals only run
        // once, they stay frozen part-faded and the page looks blank.
        target.scrollIntoView({ behavior: changedRoute ? 'auto' : 'smooth' });
      } else if (frames++ < 10) {
        requestAnimationFrame(findTarget);
      }
    };
    requestAnimationFrame(findTarget);
  }, [pathname, hash]);

  return null;
};

function App() {
  const navigate = useNavigate();

  const [currentDeck, setCurrentDeck] = useState<Deck | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormType, setContactFormType] = useState<FormType>('contact');

  const openForm = (type: FormType) => {
    setContactFormType(type);
    setShowContactForm(true);
  };

  const handleStakeholderSelect = (stakeholder: StakeholderType) => {
    const deck = createSampleDeck(stakeholder);
    setCurrentDeck(deck);
  };

  const handleBackToMenu = () => {
    setCurrentDeck(null);
    navigate('/resources');
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  const handleExport = async (format: 'pptx' | 'pdf') => {
    if (!currentDeck) return;
    
    setIsExporting(true);
    try {
      if (format === 'pptx') {
        await exportDeck.toPPTX(currentDeck);
      } else {
        await exportDeck.toPDF(currentDeck);
      }
    } catch (error) {
      console.error(`Export failed:`, error);
      alert(`Failed to export ${format.toUpperCase()}. Please try again.`);
    }
    setIsExporting(false);
  };

  const renderDeck = (deck: Deck) => (
      <div className="relative">
        <DeckViewer deck={deck} showControls={true} />
        
        <div className="absolute top-6 left-6 z-50 flex space-x-3">
          <button
            onClick={handleBackToMenu}
            className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 text-sm font-medium text-soft-black hover:bg-white transition-colors shadow-soft"
          >
            ← Materials
          </button>
          <button
            onClick={handleBackToLanding}
            className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 text-sm font-medium text-soft-black hover:bg-white transition-colors shadow-soft"
          >
            Home
          </button>
        </div>

        {/* Export dropdown and Waitlist */}
        <div className="absolute top-6 right-6 z-50 flex space-x-3">
          <button
            onClick={() => { openForm('waitlist'); }}
            className="bg-muted-teal/90 backdrop-blur-sm rounded-xl px-4 py-2 text-sm font-medium text-white hover:bg-muted-teal transition-colors shadow-soft"
          >
            Join Waitlist
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="bg-primary/90 backdrop-blur-sm rounded-xl px-4 py-2 text-sm font-medium text-white hover:bg-primary transition-colors shadow-soft disabled:opacity-50 flex items-center space-x-2"
              disabled={isExporting}
            >
              <span>{isExporting ? 'Exporting...' : 'Export'}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showExportMenu && !isExporting && (
              <div className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-sm rounded-xl shadow-soft border border-gray-100 py-2">
                <button
                  onClick={() => {
                    handleExport('pptx');
                    setShowExportMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-soft-black hover:bg-gray-50 transition-colors"
                >
                  Export as PPTX
                </button>
                <button
                  onClick={() => {
                    handleExport('pdf');
                    setShowExportMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-soft-black hover:bg-gray-50 transition-colors"
                >
                  Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
  );

  const renderResources = () => (
      <div className="min-h-screen bg-off-white">
        <header className="py-16 px-8 text-center relative">
          <button
            onClick={handleBackToLanding}
            className="absolute top-8 left-8 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 text-sm font-medium text-soft-black hover:bg-white transition-colors shadow-soft"
          >
            ← Back to Home
          </button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-light text-soft-black mb-6">
              Stakeholders
            </h1>
            <p className="text-xl font-light text-warm-gray max-w-2xl mx-auto">
              Explore resources tailored to your role in the care journey
            </p>
          </motion.div>
        </header>

        <main className="max-w-6xl mx-auto px-8 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {STAKEHOLDER_PROFILES.map((profile, index) => (
              <motion.div
                key={profile.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <button
                  onClick={() => handleStakeholderSelect(profile.type)}
                  className="healthcare-card w-full p-8 text-left hover:shadow-gentle transition-all duration-200 hover:-translate-y-1 group"
                >
                  <div className="flex items-center mb-4">
                    <div className="text-muted-teal mr-3">
                      {profile.type === 'patient' && <PatientIcon />}
                      {profile.type === 'caregiver' && <CaregiverIcon />}
                      {profile.type === 'clinician' && <ClinicianIcon />}
                      {(profile.type === 'mental-health' || profile.type === 'enterprise') && <ClinicianIcon />}
                    </div>
                    <h3 className="text-2xl font-semibold text-deep-slate group-hover:text-muted-teal transition-colors">
                      {profile.name}
                    </h3>
                  </div>
                  
                  <p className="text-warm-gray mb-6 leading-relaxed">
                    {profile.description}
                  </p>
                  
                  <div className="space-y-2 mb-6">
                    <p className="text-sm font-medium text-deep-slate">Key Focus Areas:</p>
                    <ul className="text-sm text-warm-gray space-y-1">
                      {profile.primaryConcerns.slice(0, 3).map((concern, idx) => (
                        <li key={idx} className="flex items-center">
                          <div className="w-1.5 h-1.5 bg-muted-teal/60 rounded-full mr-2"></div>
                          {concern}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex items-center text-deep-slate font-medium group-hover:text-muted-teal transition-colors">
                    Explore Materials
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        </main>
      </div>
  );

  // Partners View — institutional, clinical and investor material, kept off
  // the consumer landing page so that each audience gets a single narrative.
  const renderPartners = () => (
      <div className="min-h-screen bg-off-white">
        <SiteHeader onJoin={() => openForm('waitlist')} />

        <section className="px-6 sm:px-8 py-20 lg:py-28">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm font-medium tracking-[0.2em] uppercase text-muted-teal mb-6">
                For clinicians, researchers &amp; partners
              </p>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-soft-black leading-[1.1] tracking-tight mb-8">
                Building the continuity layer for healthcare.
              </h1>
              <p className="text-lg sm:text-xl font-light text-warm-gray max-w-2xl mx-auto leading-relaxed mb-10">
                HealthyMitra is a consented, auditable record of what happens
                between consultations, shared across a patient&rsquo;s care circle.
                Developed with researchers from IIT Jodhpur and AIIMS Jodhpur.
              </p>
              <button
                onClick={() => navigate('/resources')}
                className="minimal-button text-lg px-8 py-4 rounded-2xl bg-white text-deep-slate border border-warm-gray/25 hover:bg-gray-50"
              >
                Browse stakeholder materials
              </button>
            </motion.div>
          </div>
        </section>

        {/* WHY EXISTING SYSTEMS FAIL */}
        <Section id="the-gap" className="bg-gradient-to-br from-off-white to-gray-50">
          <div className="max-w-4xl mx-auto">
            <SectionHeading>Why Existing Systems Fail</SectionHeading>

            <Reveal>
              <div className="healthcare-card p-6 sm:p-10">
                <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8">
                  <div className="pb-4 border-b-2 border-warm-gray/20 text-base sm:text-xl font-medium text-deep-slate">
                    Existing Systems
                  </div>
                  <div className="pb-4 border-b-2 border-warm-gray/20 text-base sm:text-xl font-medium text-muted-teal">
                    HealthyMitra
                  </div>
                  {[
                    ['Episodic', 'Continuous'],
                    ['Single-user', 'Multi-party'],
                    ['Fragmented', 'Structured'],
                    ['Blind AI', 'Context-aware AI'],
                    ['Untraceable communication', 'Auditable communication']
                  ].map((row) => (
                    <React.Fragment key={row[0]}>
                      <div className="py-4 border-b border-warm-gray/10 text-warm-gray text-sm sm:text-lg">
                        {row[0]}
                      </div>
                      <div className="py-4 border-b border-warm-gray/10 text-deep-slate font-medium text-sm sm:text-lg">
                        {row[1]}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </Section>

        {/* RESEARCH ORIENTATION */}
        <Section id="research">
          <div className="max-w-5xl mx-auto">
            <SectionHeading>Research-First Approach</SectionHeading>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Clinical Studies',
                  description: 'Evidence-based deployment with ongoing clinical validation'
                },
                {
                  title: 'Institutional Collaborations',
                  description: 'Working directly with leading medical institutions'
                },
                {
                  title: 'Evidence-First Deployment',
                  description: 'Every feature validated through real-world clinical use'
                }
              ].map((item, index) => (
                <Reveal key={item.title} delay={index * 0.1}>
                  <div className="healthcare-card p-8 text-left h-full">
                    <h3 className="text-lg font-semibold text-deep-slate mb-4">
                      {item.title}
                    </h3>
                    <p className="text-warm-gray leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        {/* PARTNER / INVESTOR CTA */}
        <Section id="work-with-us" className="bg-gradient-to-br from-off-white to-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <SectionHeading subtitle="Tell us which conversation you would like to start.">
              Work With Us
            </SectionHeading>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12">
              {[
                { title: 'Pilot partnerships', type: 'pilot' },
                { title: 'Clinical collaborations', type: 'clinical' },
                { title: 'Research collaborations', type: 'research' },
                { title: 'Strategic investment', type: 'investment' }
              ].map((item, index) => (
                <Reveal key={item.type} delay={index * 0.08}>
                  <button
                    onClick={() => { openForm(item.type as any); }}
                    className="healthcare-card p-6 text-center w-full h-full hover:shadow-gentle transition-all duration-200 hover:-translate-y-1"
                  >
                    <p className="text-deep-slate font-medium">{item.title}</p>
                  </button>
                </Reveal>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                onClick={() => { openForm('contact'); }}
                className="animated-text-link text-lg text-deep-slate"
              >
                Contact Us
              </button>
            </div>

            <p className="text-sm text-warm-gray/70 tracking-wide mt-12">Patent Pending</p>
          </div>
        </Section>

        <SiteFooter />
      </div>
  );

  // How It Works View — the consideration-stage detail that would overload
  // the landing page: what a credit is, how the care circle works, and the
  // questions families ask before subscribing.
  const renderHowItWorks = () => (
      <div className="min-h-screen bg-off-white">
        <SiteHeader onJoin={() => openForm('waitlist')} />

        <section className="px-6 sm:px-8 py-20 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-soft-black leading-[1.1] tracking-tight mb-8">
                How HealthyMitra works
              </h1>
              <p className="text-lg sm:text-xl font-light text-warm-gray leading-relaxed">
                Credits, care circles and clinician subscriptions, explained
                plainly. Here is exactly what you are paying for.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CREDITS */}
        <Section id="credits" className="bg-gradient-to-br from-off-white to-gray-50">
          <div className="max-w-3xl mx-auto">
            <p className="text-center text-sm font-medium tracking-[0.15em] uppercase text-muted-teal mb-4">
              1 credit = &#8377;1
            </p>
            <SectionHeading subtitle="Credits belong to you, not to a shared family pot.">
              What a credit is
            </SectionHeading>

            <Reveal>
              <div className="healthcare-card p-8 sm:p-10 mb-8">
                <div className="pb-8 mb-8 border-b border-warm-gray/15 text-center">
                  <span className="inline-block rounded-full bg-muted-teal text-white text-xs sm:text-sm font-semibold tracking-[0.12em] uppercase px-4 py-1.5 mb-5">
                    Early-access offer &mdash; 30% off
                  </span>
                  <div className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-2">
                    <span className="text-4xl sm:text-5xl font-light text-soft-black">1,500 credits</span>
                    <span className="text-2xl text-warm-gray">=</span>
                    <span className="text-2xl font-light text-warm-gray/60 line-through">&#8377;1,500</span>
                    <span className="text-4xl sm:text-5xl font-light text-muted-teal">&#8377;999</span>
                  </div>
                  <p className="text-sm font-medium text-forest-green mt-4">
                    You save &#8377;501
                  </p>
                </div>

                <div className="space-y-6">
                  <p className="text-lg text-deep-slate leading-relaxed">
                    Credits are priced one-to-one with the rupee. 1,500 credits
                    normally cost &#8377;1,500; early-access members pay &#8377;999
                    for the same balance &mdash; about 30% off.
                  </p>
                  <p className="text-lg text-deep-slate leading-relaxed">
                    You buy credits into your own account. You can spend them on
                    yourself and on any patient in your care circle &mdash; so one
                    person can quietly carry the cost for a parent, without the
                    family needing to organise a shared wallet.
                  </p>
                  <p className="text-warm-gray leading-relaxed">
                    Patients can hold credits of their own as well. There is no
                    claim to file and nothing to get rejected.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="healthcare-card p-8 sm:p-10">
                <h3 className="text-2xl font-medium text-deep-slate mb-6">
                  What you spend them on
                </h3>

                <p className="text-lg text-deep-slate leading-relaxed mb-6">
                  A clinician subscription costs{' '}
                  <span className="font-medium">1,500 credits a month</span> and
                  covers <span className="font-medium">one clinician for one
                  patient</span>. It is not a family plan &mdash; each patient you
                  care for is subscribed separately.
                </p>

                <ul className="space-y-4 mb-8">
                  {[
                    'The clinician joins that patient’s care circle',
                    'Two appointments are included',
                    'Direct chat, voice and video with that clinician',
                    'Their reports post to the circle, where every caregiver can see them'
                  ].map((item) => (
                    <li key={item} className="flex items-start text-deep-slate">
                      <span className="mt-1 shrink-0">
                        <CheckIcon />
                      </span>
                      <span className="ml-3">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-warm-gray/15 pt-6 space-y-3">
                  <p className="text-warm-gray leading-relaxed">
                    Appointments beyond the two included are paid for in credits.
                  </p>
                  <p className="text-warm-gray leading-relaxed">
                    Each clinician sets their own availability and response times.
                    A subscription opens the channel to them; it does not promise
                    a reply within a fixed window.
                  </p>
                  {/* TODO: confirm before launch — per-appointment credit cost,
                      whether renewal is automatic or manual, and whether unused
                      credits expire or roll over. */}
                  <p className="text-sm text-warm-gray/80 italic pt-2">
                    Per-appointment pricing and renewal details are being finalised
                    ahead of launch.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </Section>

        {/* CARE CIRCLE */}
        <Section id="care-circle">
          <div className="max-w-4xl mx-auto text-center">
            <SectionHeading subtitle="Every member contributes differently. Patients heal. Caregivers support. Clinicians guide.">
              One Care Circle. One Shared Health Journey.
            </SectionHeading>

            <Reveal delay={0.15}>
              <svg
                className="w-full max-w-2xl mx-auto h-auto"
                viewBox="0 0 600 500"
                role="img"
                aria-label="Care circle diagram showing patient, caregiver and clinician connected through HealthyMitra"
              >
                {/* Large outer circle */}
                <circle cx="300" cy="250" r="250" fill="none" stroke="#5D8A8A" strokeWidth="2" strokeDasharray="5,5"/>

                {/* Connection lines to center */}
                <line x1="300" y1="90" x2="300" y2="195" stroke="#6B8E65" strokeWidth="2"/>
                <line x1="190" y1="360" x2="261" y2="289" stroke="#6B8E65" strokeWidth="2"/>
                <line x1="410" y1="360" x2="339" y2="289" stroke="#6B8E65" strokeWidth="2"/>

                {/* HealthyMitra center */}
                <circle cx="300" cy="250" r="55" fill="#6B8E65" fillOpacity="0.1" stroke="#6B8E65" strokeWidth="2"/>
                <text x="300" y="256" textAnchor="middle" className="text-base fill-forest-green font-semibold">HealthyMitra</text>

                {/* Patient */}
                <circle cx="300" cy="50" r="40" fill="none" stroke="#5D8A8A" strokeWidth="2"/>
                <text x="300" y="56" textAnchor="middle" className="text-base fill-deep-slate font-medium">Patient</text>

                {/* Caregiver */}
                <circle cx="162" cy="388" r="40" fill="none" stroke="#5D8A8A" strokeWidth="2"/>
                <text x="162" y="394" textAnchor="middle" className="text-base fill-deep-slate font-medium">Caregiver</text>

                {/* Clinician */}
                <circle cx="438" cy="388" r="40" fill="none" stroke="#5D8A8A" strokeWidth="2"/>
                <text x="438" y="394" textAnchor="middle" className="text-base fill-deep-slate font-medium">Clinician</text>
              </svg>
            </Reveal>
          </div>
        </Section>

        {/* QUESTIONS */}
        <Section id="questions" className="bg-gradient-to-br from-off-white to-gray-50">
          <div className="max-w-3xl mx-auto">
            <SectionHeading>Questions families ask</SectionHeading>

            <div className="space-y-4">
              {[
                {
                  q: 'Do I need a separate subscription for each parent?',
                  a: 'Yes. A subscription covers one clinician for one patient, so each person you care for is subscribed separately.'
                },
                {
                  q: 'Does my father have to agree to being added?',
                  a: 'Yes. You send the request and he approves it. Adding him to your care circle also makes you a caregiver in his.'
                },
                {
                  q: 'Can he hold credits of his own?',
                  a: 'Yes. Patients can buy and hold their own credits, and either balance can pay.'
                },
                {
                  q: 'Who can see my family’s health information?',
                  a: 'The patient, the caregivers in their circle, and any clinician subscribed to them. Clinician reports post to that circle, so every caregiver sees the same thing.'
                },
                {
                  q: 'How quickly will the clinician respond?',
                  a: 'That is up to each clinician. A subscription opens direct chat, voice and video with them; it does not guarantee a reply within a set time.'
                },
                {
                  q: 'What happens to credits I do not use?',
                  a: null
                },
                {
                  q: 'Can I cancel at any time?',
                  a: null
                }
              ].map((item, index) => (
                <Reveal key={item.q} delay={index * 0.06}>
                  <div className="healthcare-card p-6 sm:p-7 text-left">
                    <p className="text-deep-slate font-medium mb-2">{item.q}</p>
                    {item.a ? (
                      <p className="text-warm-gray leading-relaxed">{item.a}</p>
                    ) : (
                      /* TODO: answer once rollover and cancellation terms are set. */
                      <p className="text-warm-gray/70 italic leading-relaxed">
                        Being finalised ahead of launch.
                      </p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.45}>
              <p className="text-center text-warm-gray mt-10">
                Something we have not covered?{' '}
                <button
                  onClick={() => { openForm('contact'); }}
                  className="animated-text-link text-deep-slate"
                >
                  Ask us directly
                </button>
                .
              </p>
            </Reveal>
          </div>
        </Section>

        <SiteFooter />
      </div>
  );

  const renderLanding = () => (
    <div className="min-h-screen bg-off-white">
      <SiteHeader onJoin={() => openForm('waitlist')} />

      {/* 1. HERO */}
      <section>
        {/* Asymmetric padding: with the wordmark lockup and the bullet list
            commented out the hero is short, so it needs height above the
            headline without leaving a void before the next section. */}
        <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-24 pb-16 lg:pt-36 lg:pb-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-col items-center mb-10">
              {/* Wordmark lockup: the name and the logo sit on one shared
                  baseline, with the logo set about 1.3x the type height. */}
              {/* <div className="flex items-baseline justify-center gap-2 mb-10">
                <span className="text-[2.5rem] sm:text-[3.5rem] leading-none font-light text-soft-black tracking-tight">
                  HealthyMitra
                </span>
                <img
                  src="/images/Company logo butterfly.png"
                  alt=""
                  aria-hidden="true"
                  className="h-[3.25rem] sm:h-[4.5rem] w-auto shrink-0"
                />
              </div> */}
              <h1 className="text-4xl sm:text-6xl md:text-7xl font-light text-soft-black leading-[1.05] tracking-tight max-w-4xl">
                Care for your loved ones at{' '}
                <span className="font-medium text-muted-teal">&#8377;35/day</span>.
              </h1>
            </div>

            <p className="text-xl sm:text-2xl font-light text-soft-black mb-6 leading-snug max-w-3xl mx-auto">
              Keep your parents&rsquo; doctor connected to their care, and stay part
              of what happens between consultations.
            </p>

            <p className="text-lg font-light text-warm-gray mb-12 max-w-2xl mx-auto leading-relaxed">
              Credits in your account pay for your family&rsquo;s care &mdash; and
              for your own.
            </p>

            {/* <ul className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-8 justify-center items-center mb-12">
              {[
                'Your credits, for your family',
                'Their clinician, inside the care circle',
                'No paperwork. No claim rejection.'
              ].map((item) => (
                <li key={item} className="flex items-center text-base text-deep-slate">
                  <CheckIcon />
                  <span className="ml-2">{item}</span>
                </li>
              ))}
            </ul> */}

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mb-8">
              <button
                onClick={() => { openForm('waitlist'); }}
                className="minimal-button text-lg px-8 py-4 rounded-2xl bg-muted-teal hover:bg-muted-teal/90 w-full sm:w-auto"
              >
                Join Early Access
              </button>
              <a
                href="#how-it-works"
                className="minimal-button text-lg px-8 py-4 rounded-2xl bg-white text-deep-slate border border-warm-gray/25 hover:bg-gray-50 w-full sm:w-auto text-center"
              >
                See how it works
              </a>
            </div>

            <p className="text-sm font-light text-warm-gray/70 tracking-wide">
              Patent Pending
            </p>
          </motion.div>
        </div>

      </section>

      {/* 2. HOW IT WORKS — four steps mirroring the real flow: build the
             circle, fund it, subscribe to the clinician, stay in the loop.
             Credit values and appointment rules live on the detail view. */}
      <Section id="how-it-works" className="bg-gradient-to-br from-off-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading subtitle="Four steps from worrying at a distance to being part of the care.">
            How it works
          </SectionHeading>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              {
                icon: <ShareIcon />,
                step: 'Step 1',
                title: 'Build your care circle',
                description: 'Add family members you look after. They approve, and you become their caregiver.'
              },
              {
                icon: <WalletIcon />,
                step: 'Step 2',
                title: 'Add credits',
                description: 'Credits sit in your account and pay for your family’s care, and your own.'
              },
              {
                icon: <ClinicianIcon />,
                step: 'Step 3',
                title: 'Subscribe to their clinician',
                description: 'That doctor joins the patient’s circle, with appointments and direct chat, voice and video.'
              },
              {
                icon: <AccessIcon />,
                step: 'Step 4',
                title: 'Stay in the loop',
                description: 'Updates and clinician reports post to the circle, where every caregiver can see them.'
              }
            ].map((item, index) => (
              <Reveal key={item.title} delay={index * 0.1}>
                <div className="healthcare-card p-6 lg:p-7 text-left h-full">
                  <div className="text-muted-teal mb-5">{item.icon}</div>
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-teal mb-3">
                    {item.step}
                  </p>
                  <h3 className="text-xl font-medium text-deep-slate mb-3">
                    {item.title}
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.45}>
            <p className="text-center mt-12">
              <Link
                to="/how-it-works#credits"
                className="animated-text-link text-lg text-deep-slate"
              >
                See how credits work &rarr;
              </Link>
            </p>
          </Reveal>
        </div>
      </Section>
      {/* 3. WHO IT'S FOR */}
      <Section id="who-its-for">
        <div className="max-w-6xl mx-auto">
          <SectionHeading subtitle="From family caregiving to clinical research &mdash; wherever care continues outside the clinic.">
            Who it&rsquo;s for
          </SectionHeading>

          {/* Six-column track so the seven cards sit 2 / 3 / 2 on desktop:
              the outer rows span three columns each, the middle row two. The
              centre slot of the middle row (index 3) is the most-seen card, so
              the newest, most distinctive offer sits there and is emphasised. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8">
            {[
              {
                title: 'Support Elderly Parents',
                description: 'Track consultations and stay involved.'
              },
              {
                title: 'Manage Chronic Conditions',
                description: 'Diabetes, hypertension, cardiac recovery.'
              },
              {
                title: 'Recover After Hospitalization',
                description: 'Stay connected during recovery.'
              },
              {
                eyebrow: 'AI-powered mediation',
                featured: true,
                title: 'Protect Family Relationships',
                description: 'When a family disagrees on a loved one’s treatment, AI-assisted mediation keeps the care circle talking and aligned.'
              },
              {
                title: 'Support Mental Health Journeys',
                description: 'Family-inclusive care when appropriate.'
              },
              {
                title: 'Manage Paediatric Care',
                description: 'Keep parents, paediatrician and caregivers aligned on a child’s treatment and follow-ups.'
              },
              {
                title: 'Run Clinical Studies',
                description: 'For research organisations: don’t just track adherence. Cut confounding variables with higher-quality data, and keep participants enrolled to the end.'
              }
            ].map((item: { title: string; description: string; eyebrow?: string; featured?: boolean }, index) => (
              <Reveal
                key={item.title}
                delay={index * 0.08}
                className={index < 2 || index > 4 ? 'lg:col-span-3' : 'lg:col-span-2'}
              >
                <div
                  className={`healthcare-card p-8 text-center h-full flex flex-col justify-center ${
                    item.featured ? 'ring-2 ring-muted-teal/40 bg-muted-teal/[0.03]' : ''
                  }`}
                >
                  {item.eyebrow && (
                    <p className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-teal mb-3">
                      {item.eyebrow}
                    </p>
                  )}
                  <h3 className="text-2xl font-medium text-deep-slate mb-3">
                    {item.title}
                  </h3>
                  <p className="text-warm-gray text-lg leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* 4. THE PROBLEM */}
      <Section className="bg-gradient-to-br from-off-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-soft-black mb-8 leading-tight max-w-4xl mx-auto">
              Healthcare becomes fragmented after the appointment.
            </h2>
            <p className="text-xl text-muted-teal font-medium">
              HealthyMitra keeps everyone connected.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <PatientIcon />,
                title: 'Patients forget.',
                detail: 'Instructions fade, medications get missed, and questions go unasked.'
              },
              {
                icon: <CaregiverIcon />,
                title: 'Caregivers struggle to coordinate.',
                detail: 'Families carry appointments, reports and reminders without support.'
              },
              {
                icon: <ClinicianIcon />,
                title: 'Clinicians lose visibility.',
                detail: 'What happens between visits never reaches the consultation room.'
              }
            ].map((item, index) => (
              <Reveal key={item.title} delay={index * 0.1}>
                <div className="healthcare-card p-8 text-left h-full">
                  <div className="text-muted-teal mb-6">{item.icon}</div>
                  <h3 className="text-xl font-medium text-soft-black mb-3">
                    {item.title}
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* 5. WHY TRUST US — institutional backing and the safety commitments,
             merged and moved up. Credibility was previously stranded near the
             bottom of the page. */}
      <Section id="trust">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-soft-black leading-tight mb-6">
              Why families trust us
            </h2>
            <p className="text-xl sm:text-2xl font-medium text-deep-slate mb-3">
              Developed by researchers from IIT Jodhpur &nbsp;&middot;&nbsp; AIIMS Jodhpur
            </p>
            <p className="text-lg text-warm-gray">
              Supported through India&rsquo;s innovation ecosystem
            </p>
          </Reveal>

          {/* The marks are not recognisable on their own, so each carries its
              name underneath and is given enough height to be legible. */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-10 items-start justify-items-center mb-16">
              {[
                { src: '/images/AIIMS high.jpg', alt: 'All India Institute of Medical Sciences, Jodhpur', name: 'AIIMS Jodhpur' },
                { src: '/images/iitj-new-large.jpg', alt: 'Indian Institute of Technology Jodhpur', name: 'IIT Jodhpur' },
                { src: '/images/TISC.jpg', alt: 'Technology Innovation and Startup Centre', name: 'TISC' },
                { src: '/images/ihub_drishti_logo.jpg', alt: 'TIH iHub Drishti Foundation', name: 'iHub Drishti' },
                { src: '/images/Istart_color.png', alt: 'iStart Rajasthan', name: 'iStart Rajasthan' },
                { src: '/images/HY.png', alt: 'HealYantra', name: 'HealYantra' }
              ].map((logo) => (
                <div key={logo.src} className="flex flex-col items-center text-center w-full">
                  <div className="flex items-center justify-center h-20 sm:h-24 mb-3">
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className="max-h-20 sm:max-h-24 max-w-full object-contain hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-xs sm:text-sm text-warm-gray leading-snug">
                    {logo.name}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'You decide who sees what',
                description: 'Nothing is shared with your care circle without your consent.'
              },
              {
                title: 'Every action is on the record',
                description: 'You can see who accessed your family’s information, and when.'
              },
              {
                title: 'A person is always accountable',
                description: 'Clinical judgement stays with clinicians. AI assists, it does not decide.'
              }
            ].map((item, index) => (
              <Reveal key={item.title} delay={index * 0.08}>
                <div className="healthcare-card p-8 text-left h-full">
                  <div className="text-muted-teal mb-6">
                    <ShieldIcon />
                  </div>
                  <h3 className="text-xl font-medium text-deep-slate mb-3">
                    {item.title}
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* 6. PRICING — priced per clinician, per patient. The daily figure is
             the headline the market understands; the credit cost and the
             coverage limit sit next to it so the offer cannot be misread. */}
      <Section id="pricing" className="bg-gradient-to-br from-off-white to-gray-50">
        <div className="max-w-xl mx-auto">
          <SectionHeading subtitle="Subscribe to the clinician who looks after the person you care for.">
            Care Circle Membership
          </SectionHeading>

          <Reveal>
            <div className="healthcare-card p-8 sm:p-10 text-center">
              {/* The badge states the offer outright so the struck-through
                  price reads as a discount at a glance, not just a number. */}
              <span className="inline-block rounded-full bg-muted-teal text-white text-xs sm:text-sm font-semibold tracking-[0.12em] uppercase px-4 py-1.5 mb-6">
                Early-access offer &mdash; 30% off
              </span>

              <div className="flex items-baseline justify-center gap-3">
                <span className="text-2xl font-light text-warm-gray/60 line-through">&#8377;1,500</span>
                <span className="text-5xl sm:text-6xl font-light text-soft-black">&#8377;999</span>
                <span className="text-xl text-warm-gray">/month</span>
              </div>

              <p className="text-warm-gray mt-3">
                 covers one clinician for one patient
              </p>
              <p className="text-sm font-medium text-forest-green mt-2 mb-10">
                You save &#8377;501 &mdash; for members who join early access.
              </p>

              <ul className="space-y-4 text-left mb-8">
                {[
                  'Clinician joins the patient’s care circle',
                  'Two appointments included',
                  'Direct chat, voice and video with that clinician',
                  'Clinician reports posted to the circle for every caregiver'
                ].map((feature) => (
                  <li key={feature} className="flex items-start text-lg text-deep-slate">
                    <span className="mt-1 shrink-0">
                      <CheckIcon />
                    </span>
                    <span className="ml-3">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Stated plainly rather than buried: clinicians set their own
                  availability, and appointments beyond the included two are
                  charged in credits. */}
              <p className="text-sm text-warm-gray/80 mb-10 leading-relaxed">
                Further appointments cost credits.
              </p>

              <button
                onClick={() => { openForm('waitlist'); }}
                className="minimal-button text-xl px-12 py-5 rounded-2xl bg-muted-teal hover:bg-muted-teal/90 w-full sm:w-auto"
              >
                Join Early Access
              </button>

              {/* Both land on the relevant section of the detail page rather
                  than its top, so the answer is on screen on arrival. */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-5 justify-center items-center">
                <Link
                  to="/how-it-works#credits"
                  className="animated-text-link text-warm-gray"
                >
                  See how credits work
                </Link>
                <span aria-hidden="true" className="hidden sm:inline text-warm-gray/40">
                  &middot;
                </span>
                <Link
                  to="/how-it-works#questions"
                  className="animated-text-link text-warm-gray"
                >
                  Have a question?
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );

  return (
    <>
      <ScrollManager />

      <Routes>
        <Route path="/" element={renderLanding()} />
        <Route path="/how-it-works" element={renderHowItWorks()} />
        <Route path="/partners" element={renderPartners()} />
        {/* The deck is a state overlay on the resources route: it only exists
            once a stakeholder has been chosen, so it has no URL of its own. */}
        <Route
          path="/resources"
          element={currentDeck ? renderDeck(currentDeck) : renderResources()}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Mounted once, above the routes, so the modal survives navigation. */}
      <ContactForm
        isOpen={showContactForm}
        onClose={() => setShowContactForm(false)}
        formType={contactFormType}
      />
    </>
  );
}

// BrowserRouter has to sit above the component calling useNavigate.
const AppWithRouter = () => (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

export default AppWithRouter;
